// tslint:disable:no-reference
/// <reference path="../../etc/designs/base/clientlibs/shared/libraries/SCS.utils.js.d.ts" />
// tslint:enable:no-reference

import { patch } from 'bootstrap/load-snabbdom';
import { VNode } from 'snabbdom/vnode';

import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import { isMobilePage } from 'lib/util/helpers';
import { getProfile } from 'lib/personalization/profile';

import { SubRatingCategories, Rating, SubRatingCategory, RatingSummary } from './Rating';
import { Stars } from './Tc.Module.RatingList';
import { Family } from './Product';
import { simpleSubscribe, ExposedProductInfo } from './Tc.Module.EcommerceProductDataHub';


interface AggretatedSummaryData {
	avg : number;
	category : SubRatingCategory;
	count : number;
	sum : number;
}

function SubcategoryRatings({summarySubData} : {
	summarySubData : AggretatedSummaryData[];
}) {
	return summarySubData.map(summaryInfo => {
		return <a attrs-class="rating-stars sub-ratings">
			<Stars rating={summaryInfo.avg} sdx />
			<span>{summaryInfo.category.getName()} ({summaryInfo.count})</span>
		</a>;
	});
}

/**
 * Prepare summary sub rating data for insertion
 *
 * @param ratingSubSummary {Object} Returned JSON object from ajax call with summary sub ratings
 */
function aggregateSubRatingSummaryData(
	subratingCategories : SubRatingCategories,
	ratingList : Rating[],
) : AggretatedSummaryData[] {
	// FIXME: Maybe iterating the ratings first and the categories inside is less overhead. But it also needs more state…
	return subratingCategories.getCategories().map(category => {
		const ratingValues = ratingList.map(rating => {
			// Find the rating for the given category
			return rating.getCategorySubratings().forCategoryWithId(category.getId());
		}).filter(ratingValue => {
			// Only include the rating if > 0
			return ratingValue > 0;
		});
		// Sum up all the ratingValues
		const sum = ratingValues.reduce((a, b) => {
			return a + b;
		}, 0);
		// Count the ratingValues
		const count = ratingValues.length;

		// Return aggregate summary data for each category
		return {
			avg: sum / count,
			category, count, sum,
		};
	}).filter(summaryInfo => {
		// Remove categories with ratings <= 0
		return summaryInfo.avg > 0;
	});
}

/**
 * Rating overview component
 *
 * @author Branislav Maksin
 * @namespace Tc.Module
 * @class RatingsOverview
 * @extends Tc.Module
 */
export default class RatingOverview extends Module {
	private subRatingsPlaceholder! : HTMLElement | VNode;
	private initialized! : boolean;

	/**
	 * Does all the work.
	 *
	 * @method on
	 * @param {Function} callback function
	 * @return void
	 */
	public on(callback : Hook) {
		const $ctx = this.$ctx;
		const loginRequired = $ctx.data('login-required');
		const loginUrl = $ctx.data('login-url');

		this.subRatingsPlaceholder = $ctx.find('.sub-ratings-wrapper')[0];

		// Subscribe to products private channel and get data
		this.sandbox.subscribe('dynamicProducts', this);
		this.sandbox.subscribe('rarev', this);

		// Add click event for open form button
		this.$ctx.on('click', '.open-overlay', async e => {
			e.preventDefault();

			if(!loginRequired || (await getProfile()).getUser()) {
				this.fire('openRatingForm', $(e.currentTarget), ['dynamicProducts']);
				this.fire('addTracking', 'rate', ['dynamicProducts']);
			} else {

				const $tabNavigationContent = $('.modTabNavigationContent').has($ctx[0]);
				const activeTabIndex = this.$ctx
					.closest('.modTabNavigation')
					.children('.modTabNavigationContent')
					.index($tabNavigationContent) + 1;

				if(activeTabIndex === 0) {
					window.location.href = loginUrl.replace('.tab_index.', '.');
				} else {
					window.location.href = loginUrl.replace('tab_index', 't' + activeTabIndex);
				}
			}
		});

		this.initialized = false;

		// tslint:disable-next-line:no-unbound-method
		simpleSubscribe(this, this.processProductsIDs);

		callback();
	}

	/**
	 * Process products IDs and get products JSONs data
	 *
	 */
	public processProductsIDs(data : ExposedProductInfo) {
		const $ctx = this.$ctx;

		if(this.initialized) {
			return;
		}

		const family = data.family;

		// Get product data
		if(!family.getId()) {
			this.log('Family doesn’t have any IDs');
			$ctx.removeClass('scs-loadingRotator').addClass('error');

			return;
		}

		if($ctx.hasClass('error')) {
			$ctx.removeClass('error').addClass('scs-loadingRotator');
		}

		const ratings = family.getRatings();
		if(!ratings.loaded()) {
			// Register callback for when rating data is loaded
			// tslint:disable-next-line:no-floating-promises
			ratings.load().done(this.processProductsIDs.bind(this, data));

			return;
		}

		this.initialized = true;


		const summary = ratings.getSummary();
		this.showRatingSummary(summary);

		const summarySubData = aggregateSubRatingSummaryData(ratings.getSubRatingCategories()!, ratings.getRatings()!);
		this.showSubRatingSummary(summarySubData);

		this.sendDataToForm(family);

		$ctx.removeClass('scs-loadingRotator');
	}

	/**
	 * Show form button when everything is ready
	 *
	 * @param {Object} data With
	 * • {Boolean} show: Should we show button?
	 * • {Boolean} success: Should we show success message?
	 */
	public onShowFormButton(data : {
		show : boolean | undefined;
		success : boolean | undefined;
	}) {
		const $ctx = this.$ctx;

		// Show/hide open form button
		$ctx.find('.open-form').toggleClass('hidden', data.show === false);
		$ctx.find('.statusSuccessful').toggleClass('hidden', data.success !== true);
	}

	/**
	 * Insert the summary data into DOM elements
	 *
	 * @param summaryData {Object} Final summary data
	 */
	private showRatingSummary(summaryData : RatingSummary) {
		const $ctx = this.$ctx;
		const $ratingStarHolder = $ctx.find('.rating-stars:first');

		// Get needed info
		const rating = summaryData.getAverage(true);
		const recommendationPercentage = summaryData.getRecommendationPercentage(true);
		const count = summaryData.count();

		// Set the rating star summary
		const $ratingStars = $ratingStarHolder.find('> i');
		const $ratingStarsCount = $ratingStarHolder.find('> span');
		$ratingStars.addClass('icon-082-star').filter(`:lt(${rating})`)
			.removeClass('icon-082-star')
			.addClass('icon-228-star-filled');
		$ratingStarsCount.text(`(${count})`);

		// Show recommendation percentage
		$ctx.find('.recommendation')
			.toggleClass('hidden', recommendationPercentage < 1)
			.find('> p .recommendation-percentage')
			.text(`${recommendationPercentage}%`);
	}

	/**
	 * Insert the summary data into DOM elements
	 *
	 * @param summarySubData {Object} Final sub summary data
	 */
	private showSubRatingSummary(summarySubData : AggretatedSummaryData[]) {
		this.subRatingsPlaceholder = patch(
			this.subRatingsPlaceholder,
			<div attrs-class="sub-ratings-wrapper" class-hidden={!summarySubData.length}>
				<SubcategoryRatings summarySubData={summarySubData} />
			</div>,
		);
	}

	/**
	 * Send the data to review form
	 */
	private sendDataToForm(family : Family) {
		let title = family.getFamilyName();

		// Get product title
		if (isMobilePage()) {
			title = this.$ctx.closest('.stageMobile').find('h1').text() || title;
		} else {
			title = this.$ctx.closest('.content').find('h1').text() || title;
		}

		// Send the data
		this.fire('getDataFromParent', {family, title}, ['rarev']);
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.RatingOverview.tsx